import type { Observation } from "../api";
import type { TZ } from "../lib/time";
import { formatTimeShort } from "../lib/time";

interface Props {
  observations: Observation[];
  onAircraftSelect: (hex: string) => void;
  tz: TZ;
  isSearching: boolean;
}

interface AircraftGroup {
  hex: string;
  registration: string | null;
  callsign: string | null;
  lastSeen: number;
  count: number;
}

export default function AircraftList({ observations, onAircraftSelect, tz, isSearching }: Props) {
  if (isSearching) {
    return (
      <div className="rounded border border-border divide-y divide-border overflow-hidden" aria-busy="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="px-3 py-2.5 flex items-center justify-between gap-2 animate-pulse">
            <div className="flex flex-col gap-1.5 flex-1 min-w-0">
              <div className="h-3.5 w-24 rounded bg-muted" />
              <div className="h-2.5 w-36 rounded bg-muted" />
            </div>
            <div className="h-2.5 w-12 rounded bg-muted shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  if (observations.length === 0) return null;

  const map = new Map<string, AircraftGroup>();
  for (const obs of observations) {
    const g = map.get(obs.hex);
    if (!g) {
      map.set(obs.hex, {
        hex: obs.hex,
        registration: obs.registration ?? null,
        callsign: obs.flight ?? null,
        lastSeen: obs.observed_at,
        count: 1,
      });
    } else {
      if (obs.observed_at > g.lastSeen) {
        g.lastSeen = obs.observed_at;
        if (obs.flight) g.callsign = obs.flight;
        if (obs.registration) g.registration = obs.registration;
      }
      g.count++;
    }
  }

  const sorted = [...map.values()].sort((a, b) => b.lastSeen - a.lastSeen);

  return (
    <div className="rounded border border-border divide-y divide-border overflow-hidden">
      {sorted.map((aircraft) => {
        const title =
          aircraft.callsign ?? aircraft.registration ?? aircraft.hex.toUpperCase();
        const meta = [
          aircraft.callsign && aircraft.registration ? aircraft.registration : null,
          `${aircraft.count} obs`,
          formatTimeShort(aircraft.lastSeen, tz),
        ]
          .filter(Boolean)
          .join(" · ");

        return (
          <button
            key={aircraft.hex}
            className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center justify-between gap-2"
            onClick={() => onAircraftSelect(aircraft.hex)}
          >
            <div className="min-w-0">
              <div className="font-semibold text-sm text-foreground truncate">{title}</div>
              <div className="text-[0.65rem] text-muted-foreground mt-0.5 truncate">{meta}</div>
            </div>
            <div className="shrink-0 text-[0.65rem] text-muted-foreground font-mono">
              {aircraft.hex.toUpperCase()}
            </div>
          </button>
        );
      })}
    </div>
  );
}
